import React, { PropsWithChildren, useMemo, useState } from 'react';

import Icon from '@mdi/react';
import {
    Box,
    Button,
    Divider,
    IconButton,
    LinearProgress,
    ListItemIcon,
    Menu,
    MenuItem,
    PropTypes,
    Typography
} from '@mui/material';
import styled from 'styled-components';
import { SvgIconComponent } from '@material-ui/icons';
import { MoreHoriz } from '@mui/icons-material';
import { analyticsProps } from '@class-ui/insights';
import ClassLogoForHeader from '../../icons/ClassLogoForHeader';

const Container = styled(Box)`
    display: flex;
    flex-direction: column;
`;

const CenteringWrapper = styled(Box)`
    display: flex;
    justify-content: center;
`;

const Content = styled(Box).withConfig({ shouldForwardProp: (prop) => !prop.includes('narrowStyle') })`
    padding: 28px ${({ narrowStyle }) => (narrowStyle ? '16px' : '40px')};
    max-width: 1500px;
    width: 100%;
    margin: 0 auto;
`;

const LinearProgressSpacer = styled(Box)`
    height: 4px;
`;

const Header = styled(Box).withConfig({ shouldForwardProp: (prop) => !prop.includes('narrowStyle') })`
    display: flex;
    align-items: center;
    color: white;
    background-color: ${(props) => props.theme.colors.primary.main};
    height: 64px;
    padding: 0px ${({ narrowStyle }) => (narrowStyle ? '20px' : '44px')};
`;

const HeaderImg = styled.img`
    max-height: 40px;
`;

const HeaderIcon = styled(Box)`
    flex: 0 0 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow:
        0px 3px 5px -1px rgb(0 0 0 / 20%),
        0px 6px 10px 0px rgb(0 0 0 / 14%),
        0px 1px 18px 0px rgb(0 0 0 / 12%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin-right: 4px;
    background-color: ${(props) => props.theme.colors.white};

    svg {
        color: ${(props) => props.theme.colors.primary.main};
        width: 25px;
        margin-bottom: 1px;
    }
`;

const HeaderTitle = styled(Typography)`
    flex: 1 1 content;
    flex-shrink: 1;
    margin-left: 6px;
    font-weight: 400;
    font-size: 24px;
    line-height: 28px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const HeaderControls = styled(Box)`
    display: flex;
    justify-content: flex-end;
    margin-left: auto;
`;

const StyledButton = styled(Button)`
    white-space: nowrap;
    font-size: 14px;
    margin-left: 6px;
    min-width: 42px;

    /* Remove spacing between button text and icon */
    & .MuiButton-endIcon {
        margin-left: 2px;
        margin-right: 2px;
    }

    & .MuiButton-startIcon {
        margin-right: 4px;
    }

    &.Mui-disabled {
        border-color: ${(props) => props.theme.colors.white};
        color: ${(props) => props.theme.colors.white};
        opacity: 0.3;
    }
`;

const StyledMenuButton = styled(Button)`
    padding: 0px 7px;
    white-space: nowrap;
    font-size: 14px;
    margin-left: 6px;
    min-width: 42px;
`;

const StyledMenuIcon = styled(ListItemIcon)`
    font-size: 14px;
    min-width: 30px;
`;

const StyledMenuText = styled(Typography)`
    font-size: 14px;
    font-weight: 500;
`;

const StyledIconButton = styled(IconButton)`
    font-size: 18px;
    margin-left: 6px;
    min-width: 42px;
`;

export type NavControlItem = React.ReactNode | NavControl | NavControlMenu;

/**
 * Control rendered as a button in the layout header.
 */
export interface NavControl {
    label: string;
    onClick: () => void;
    section?: number;
    icon?: SvgIconComponent;
    disabled?: boolean;
    dataTestId?: string;
    color?: PropTypes.Color | 'error';
}

/**
 * Control rendered as a button with dropdown menu in the layout header.
 */
export interface NavControlMenu {
    label: string;
    disabled?: boolean;
    menu: NavControl[];
}

/**
 * Main layout component.
 */
export interface MainLayoutProps {
    title: string;
    icon?: string;
    img?: string;
    controls?: NavControlItem[];
    loading?: boolean;
    narrowStyle?: boolean;
}

interface NavControlComponentProps {
    control: NavControl;
}

interface NavControlMenuComponentProps {
    control: NavControlMenu;
    id: number;
}

/**
 * Groups controls by section. In the order in which they are passed.
 * @param navControls - controls to group
 */
function groupNavControlsBySection(navControls: NavControl[]): { [key: number]: NavControl[] } {
    const groupedNavControls: { [key: number]: NavControl[] } = {};

    navControls.forEach((navControl) => {
        const section = navControl.section ?? 0;

        if (!groupedNavControls[section]) {
            groupedNavControls[section] = [];
        }
        groupedNavControls[section].push(navControl);
    });
    return groupedNavControls;
}

/**
 * Renders dropdown menu with controls.
 * @param menuItems - controls to render
 * @param handleMenuSelect - callback for menu item selection
 */
function renderMenuNavControls(menuItems: NavControl[], handleMenuSelect: (NavControl) => void): React.ReactNode[] {
    const groupedNavControls = groupNavControlsBySection(menuItems);
    const sections = Object.keys(groupedNavControls);

    return sections.flatMap((section, sectionIndex) => [
        sectionIndex > 0 && <Divider key={`${section}-divider`} />,
        ...groupedNavControls[section].map((menuItem, index) => (
            <MenuItem
                key={`${section}-${index}`}
                onClick={() => handleMenuSelect(menuItem)}
                disableRipple
                disabled={menuItem.disabled === undefined ? false : menuItem.disabled}
            >
                {menuItem.icon && <StyledMenuIcon>{menuItem.icon}</StyledMenuIcon>}
                <StyledMenuText color={menuItem.color ?? 'main'}>{menuItem.label}</StyledMenuText>
            </MenuItem>
        ))
    ]);
}

/**
 * Single control as a button.
 * @param {NavControl} control - control to render
 */
const NavControlComponent = (props: NavControlComponentProps) => {
    const { control } = props;

    if (control.label.length) {
        return (
            <StyledButton
                variant="outlined"
                color={control?.color ?? 'inherit'}
                size="small"
                onClick={control.onClick}
                disabled={control.disabled === undefined ? false : control.disabled}
                data-testid={control.dataTestId}
                startIcon={control.icon}
            >
                {control.label}
            </StyledButton>
        );
    }

    if (control.icon) {
        return (
            <StyledIconButton
                variant="filled"
                color={control?.color ?? 'inherit'}
                size="small"
                onClick={control.onClick}
                disabled={control.disabled === undefined ? false : control.disabled}
                data-testid={control.dataTestId}
            >
                {control.icon}
            </StyledIconButton>
        );
    }

    throw Error('Control must have at least a label or an icon defined.');
};

/**
 * Button with dropdown menu. The menu is rendered as a Material UI Menu component.
 * The section property of each NavControl in the passed {@code control.menu} is used to group menu items, separated by a divider.
 * The sections do not have to be ordered, the tool will sort them by section number.
 * @param {NavControlMenu} control - control to render
 * @param {number} id Uniquely identify dropdown menu.
 */
const NavControlMenuComponent = (props: NavControlMenuComponentProps) => {
    const { control, id } = props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuSelect = (menuItem: NavControl) => {
        setAnchorEl(null);
        menuItem.onClick();
    };

    return (
        <>
            <StyledMenuButton
                variant="outlined"
                color="inherit"
                size="small"
                disabled={control.disabled === undefined ? false : control.disabled}
                id={`nav-menu-button-${id}`}
                aria-controls={open ? `nav-menu-${id}` : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                disableElevation
                onClick={handleClick}
            >
                <MoreHoriz fontSize="large" />
                {control.label}
            </StyledMenuButton>
            <Menu
                id={`nav-menu-${id}`}
                MenuListProps={{ 'aria-labelledby': `nav-menu-button-${id}` }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                {renderMenuNavControls(control.menu, handleMenuSelect)}
            </Menu>
        </>
    );
};

/**
 * Wrap page content
 */
const MainLayout = (props: PropsWithChildren<MainLayoutProps>) => {
    const { icon, img, title, controls, loading, narrowStyle, children } = props;
    const renderedControls = useMemo(() => {
        if (!controls) {
            return;
        }

        return controls.map((control, index) => {
            if (React.isValidElement(control)) {
                return control;
            }
            if ((control as any).menu) {
                return <NavControlMenuComponent key={index} id={index} control={control as NavControlMenu} />;
            }

            return <NavControlComponent key={index} control={control as NavControl} />;
        });
    }, [controls]);

    return (
        <Container>
            <Header narrowStyle={narrowStyle}>
                {icon && (
                    <HeaderIcon>
                        <Icon path={icon} />
                    </HeaderIcon>
                )}
                {img && <HeaderImg src={img} alt="logo" />}
                {!icon && !img && <ClassLogoForHeader />}
                <HeaderTitle variant="h1">{title}</HeaderTitle>
                <HeaderControls>{renderedControls}</HeaderControls>
            </Header>

            {loading ? (
                <LinearProgress aria-busy="true" aria-live="polite" {...analyticsProps('main-layout-loading')} />
            ) : (
                <LinearProgressSpacer />
            )}

            <CenteringWrapper>
                <Content narrowStyle={narrowStyle}>{children}</Content>
            </CenteringWrapper>
        </Container>
    );
};

export default MainLayout;
